import { ExpandMore } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid,
	List,
	ListItem,
	ListItemText,
	Typography,
} from '@mui/material';
import { useMutation } from '@parkchirp/api-client';
import { FC, useCallback } from 'react';

import { DELETE_SEQUENCING_TIER } from '../../../mutations';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { SelectedParker, SequencingTier } from '../../../types/leases';
import { dayjs } from '../../../utils';
import { SimpleModal } from '../../modals';
import { SequencingParkerListItem, SequencingUnfilledSlot } from './sequencingParkerListItem';

type SequencingTierProps = {
	tier: SequencingTier;
	inBulkSequencingMode: boolean;
	maxSequenceNumber: number;
	addSelectedParker: ({ id, sequenceNumber }: SelectedParker) => void;
	removeSelectedParker: (parkerId: string) => void;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	handleWaitForResequencing: () => Promise<void>;
};

export const SequencingTierList: FC<SequencingTierProps> = ({
	tier,
	inBulkSequencingMode,
	maxSequenceNumber,
	addSelectedParker,
	removeSelectedParker,
	setShowDialog,
	setShowSnackbar,
	handleWaitForResequencing,
}) => {
	const { id: tierId, tierNumber, rateName, sublotName, parkers, maxCount, futureStartDate } = tier;

	const [deleteSequencingTier, { loading: deleteInProgress }] = useMutation(DELETE_SEQUENCING_TIER);

	const removeTier = useCallback(async () => {
		try {
			setShowDialog(null);
			await deleteSequencingTier({
				variables: { id: tierId },
				context: { apiVersion: 'v2' },
				onCompleted: async () => {
					setShowSnackbar({
						message: 'Tier successfully removed',
						severity: 'success',
						autoHideDuration: 3000,
					});

					await handleWaitForResequencing();
				},
			});
		} catch {
			setShowSnackbar({
				message: 'Failed to remove sequencing tier',
				severity: 'error',
				autoHideDuration: 5000,
			});
			setShowDialog(null);
		}
	}, [deleteSequencingTier, setShowDialog, setShowSnackbar, handleWaitForResequencing, tierId]);

	const confirmRemoveTier = useCallback(() => {
		setShowDialog(
			<SimpleModal
				title={`Remove Tier ${tierNumber}`}
				content='Are you sure?'
				handleDismiss={() => setShowDialog(null)}
				handleConfirm={removeTier}
				confirmText='Remove'
				confirmButtonColor='error'
				dismissText='Cancel'
				contentStyle={{
					fontSize: 'large',
					display: 'flex',
					justifyContent: 'center',
					margin: '3em',
					minWidth: '20em',
				}}
			/>,
		);
	}, [setShowDialog, tierNumber, removeTier]);

	const unfilledSlots = () => {
		if (parkers.length === maxCount || maxCount === 0) {
			return null;
		}

		const slots = Array.from({ length: maxCount - parkers.length });
		return slots.map((_, idx) => (
			<>
				<Divider />
				<SequencingUnfilledSlot key={`unfilled-${tierId}-${idx}`} />
			</>
		));
	};

	return (
		<Accordion defaultExpanded>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Typography variant='h6' fontWeight={600}>
					Tier {tierNumber}: {rateName}/{sublotName}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Grid container sx={{ display: 'flex', justifyContent: 'left' }}>
					<Button variant='contained' size='small' sx={{ marginBottom: '20px' }} onClick={confirmRemoveTier}>
						{deleteInProgress ? (
							<CircularProgress style={{ height: '20px', width: '20px' }} />
						) : (
							<span>Remove</span>
						)}
					</Button>
				</Grid>
				{futureStartDate && (
					<Box display='flex' justifyContent='center'>
						<Typography variant='body1'>
							Tier is not active until {dayjs(futureStartDate).utc(false).format('MMMM D, YYYY')}
						</Typography>
					</Box>
				)}
				{!futureStartDate && (
					<List
						sx={{
							width: '100%',
							bgcolor: 'background.paper',
							maxHeight: '450px',
							overflow: 'auto',
						}}
					>
						<ListItem
							key='sequencing-tier-header'
							sx={{ width: '100%' }}
							alignItems='flex-start'
							secondaryAction={<Typography variant='body1'>Sequence Number</Typography>}
						>
							<ListItemText primary='Parker' />
						</ListItem>
						<Divider />
						{parkers.map((parker) => (
							<>
								<Divider />
								<SequencingParkerListItem
									key={parker.id}
									parker={parker}
									inBulkSequencingMode={inBulkSequencingMode}
									maxSequenceNumber={maxSequenceNumber}
									disabled={deleteInProgress || inBulkSequencingMode}
									setShowSnackbar={setShowSnackbar}
									addSelectedParker={addSelectedParker}
									removeSelectedParker={removeSelectedParker}
									handleWaitForResequencing={handleWaitForResequencing}
								/>
							</>
						))}
						{unfilledSlots()}
					</List>
				)}
			</AccordionDetails>
		</Accordion>
	);
};
